import { IComments } from '@data/comments/model/comments.model'
import { Reports } from '@data/reportsTracking/model/reportsTracking.model'
import { FirmwareVersionTuple } from '@common/utils/firmwareVersion'
import {
  AssignableDeviceState,
  DeviceState,
} from '@data/device/model/deviceState'

export interface DeviceSettings {
  id: number
  dataAcqMode: string
  serverPulseControl: boolean
  pulsesPerLiter: number
  firmwareVersionDesired?: string
  timezone: string
  installDate: string
  usageStartDate: string
  monitoringStartDate: string
  deviceIDActivationDate: string
  installDateEpoch?: number
  cost: number
  currency: string
  deviceState: DeviceState
  sectorType: string
  report30Day: boolean
  report30DayCallDate?: string
  sustainabilityReport: boolean
  esgReport: boolean
  hot: boolean
  type: string
  masterDeviceIdRef?: number
  occupants: number | null
  deviceName: string
  deviceNameShort?: string
  location?: string
  uploadFreqMins: number
  uom: string
  conversionFactor: number
  monitoringEnabled: boolean
  aasEnabled: boolean
  co2Rate: number
  setUpPayment: boolean
  renewalOverdueStatus: boolean
  clientMeterId: string
  usageLoggingEnabled: boolean
  vacant: boolean
  noFlowThresholdHours: number
  missedCheckinThreshold: number
  hasValveSchedule: boolean
}

export interface Valve {
  key: ValveKey
  state: ValveState
  // From the extended valve state, which every device type reports.
  position?: ValvePosition
  command?: ValveCommandCode
  travelMs?: number
  name?: string
  manufacturer?: ValveManufacturer
  size?: ValveSize
}

// `s` — software state from the firmware command state machine.
export type ValveState = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

// States a valve can be in that warrant operator attention. Lives here rather than in a
// component file so device-list utilities (e.g. common/utils/helperFunctions.tsx) can key
// off it without importing from src/components.
export const WARNING_STATES: ValveState[] = [2, 3, 4, 5, 6]

// `p` — physical position read from the feedback lines. 2 is usually "could not be read"
// rather than "half open": feedback is powered through the coil drive line. 3 is not a
// firmware value — see the backfill note below.
export type ValvePosition = 0 | 1 | 2 | 3

// `c` — last command issued. Intent, not outcome; not cleared when a command fails. 9 is
// not a firmware value — see the backfill note below.
export type ValveCommandCode = 0 | 1 | 2 | 3 | 9

// Every device is populated on boot by the Azure background service, so valveStateExtended
// is always present. Where the device has not yet reported true extended state, that service
// synthesises a slot from the flat Vx value: `s` carries the real software state, while
// `p: 3` and `c: 9` are sentinels meaning UNKNOWN. Neither is emitted by firmware — treat
// them as "not reported yet", not as positions or commands.

export interface ValveStateExtendedAPI {
  p: ValvePosition
  s: ValveState
  c: ValveCommandCode
  t: number
}

// Slots are keyed v1Extended…v5Extended. A slot is null only when the device returned a
// short array to a valve probe; on the device response the Azure backfill guarantees all
// five. Shared by device_status.valveStateExtended and the valve probe response.
export type ValveStateExtendedSlots = {
  [Key in ValveKey as `${Lowercase<Key>}Extended`]: ValveStateExtendedAPI | null
}

export const valveExtendedKey = (key: ValveKey) =>
  `${key.toLowerCase()}Extended` as keyof ValveStateExtendedSlots

export type ValveKey = 'V1' | 'V2' | 'V3' | 'V4' | 'V5'

export type DeviceValves = Partial<Record<ValveKey, string | null>>

export type DeviceValvesUpdate = {
  dUuid: string
  valveSettings: ValveSettingsAPI[]
}

export type ValvesState = { [key in ValveKey]: ValveState }

// The domain side of the API's shared base. `DevicePublic`, `TestDevicePublic` and
// `StagedDevicePublic` all extend one base server-side, and `Device`, `TestDevice` and
// `StagedDevice` mirror that here as siblings. None extends another: a provisioned device is
// not a kind of test device, which is what the old `Device extends TestDevice` claimed, and
// it collapsed every `Device | TestDevice` union to plain `TestDevice`. Code that accepts any
// device type takes `DeviceBase`.
export interface DeviceBase {
  // Mirrors the API base field for field.
  deviceId: string
  state: string
  model: string
  deviceVendor: string
  deviceVendorName?: string
  // Required on provisioned devices only; `Device` narrows it.
  vendorId?: string
  valveOperation: boolean
  rtfOperation: boolean
  hlfOperation: boolean
  resetOperation: boolean
  fwOperation: boolean
  statusOperation: boolean
  azureDeleted?: boolean
  azureDeletedDate?: Date

  // Display data every device type carries. The API adds `device_status` per type rather
  // than on its base, but all three have one, and battery, signal and firmware are read
  // across every type — so they live here once rather than repeated on each sibling.
  deviceName: string
  firmware: string
  binFileName: string
  binFilePath: string
  connectionStatus: string
  // Nullable because device_status.power is: the mapper already defaulted these to null,
  // but DevicePowerAPI declared its members present, so those branches were unreachable and
  // the null never reached the type.
  batteryLevel: number | null
  batteryLevelExt: number | null
  batteryStatus: string
  signal: number | null
  ICCID?: string
  // Derived from the ICCID, so it is available wherever network_info is.
  mvno?: string
  networkName?: string
  lastOnline?: Date
  onBattDate?: Date
  // From the extended valve state every device type reports: `device_status.valveStateExtended`
  // on provisioned devices, `valve_state_extended` on test and staged ones. Valve names,
  // manufacturer and size are not part of it — only provisioned devices have those, merged in
  // from valve_settings.
  valves: { [key in ValveKey]: Valve }
  valveRecovery: ValveRecoveryPolicy | null
}

// Structurally identical to each other and to `DeviceBase`, as `TestDevicePublic` and
// `StagedDevicePublic` are on the API. Kept as distinct names so a staged device is never
// typed as a test device.
export interface TestDevice extends DeviceBase {}

export interface StagedDevice extends DeviceBase {}

export default interface Device extends DeviceBase {
  vendorId: string
  // Only provisioned devices have settings and an FPLMN list; the unprovisioned mapper has
  // no source for either.
  deviceSettings: DeviceSettings
  fplmnList?: string
  accountId: string
  childDeviceIds: number[]
  deviceLocationId: string
  dlId: number
  dUuid: string
  reports?: Reports
  comments?: boolean
  lastComment?: IComments
  stripeSubscriptionId?: string | null
}


export interface NewDevice {
  deviceId: string
  deviceName: string
  currency: string
  installDate: string
  cost: number
  dataAcqMode: number
  serverPulseControl: boolean
  pulsesPerLiter: number
  location?: string
  timezone: string
  sectorType: string
  deviceState: AssignableDeviceState
  hot: boolean
  uploadFreqMins: number
  uom: string
  co2Rate: number
  clientMeterId?: string
  vacant: boolean
}

export interface Timezone {
  name: string
}

export enum DeviceIDTypes {
  deviceId = 'device_id',
  dlID = 'dl_id',
  dUuid = 'd_uuid',
}

export type ValveManufacturer = 'KLD' | 'AVA'

export type ValveSize = 'DN20' | 'DN25' | 'DN32' | 'DN40' | 'DN50' | 'DN65' | 'DN80'

// Device-global, not per-valve. Only the integer crosses the wire — these labels are not in
// the OpenAPI schema, so they live here. -1 (Auto) is a reset that clears the manual
// override rather than a behaviour, so the device may resolve it to a different policy.
export type ValveRecoveryPolicy = -1 | 1 | 2 | 3

// Explicit order — object key order would sort -1 last, since only non-negative integer
// keys are ordered numerically.
export const VALVE_RECOVERY_POLICIES: ValveRecoveryPolicy[] = [-1, 1, 2, 3]

// Below this, the device firmware doesn't have the valve recovery policy, valve probe, or
// advanced valve data endpoints — requests would just fail, so these features are gated in
// the UI rather than left to error out. Shared by every valve feature with this floor so
// raising it later means changing one line, not one per feature.
export const VALVE_FEATURE_MIN_FIRMWARE: FirmwareVersionTuple = [38, 1, 0]

export interface ValveRecoveryPolicyMeta {
  label: string
  name: string
  // Lead sentence — what the policy is, in one line.
  description: string
  // Supporting detail as separate points, rendered as a bulleted list under description.
  points: string[]
}

export const VALVE_RECOVERY_POLICY_META: Record<ValveRecoveryPolicy, ValveRecoveryPolicyMeta> = {
  [-1]: {
    label: 'Auto (clear manual override)',
    name: 'AUTO',
    description: 'Clears the manual override; policy reverts to whatever the learned valve type selects.',
    points: [
      'This is a reset rather than a behaviour, so the device may resolve it to a different policy.',
    ],
  },
  // 0: {
  //   label: 'Adopt opposite of last confirmed',
  //   name: 'VALVE_RECOVERY_ADOPT_OPPOSITE',
  //   description:
  //     'Assumes the valve was deliberately moved to its other position while the system was '
  //     + 'off, and goes with that.',
  //   points: [
  //     'Drives fully to the opposite end and settles there.',
  //     'Whatever it finds becomes the new confirmed position — no attempt is made to restore the original.',
  //     'Good for trusting a deliberate manual change, but a valve that simply drifted can end up '
  //     + 'fully open when it should still be closed.',
  //   ],
  // },
  1: {
    label: 'Fail closed',
    name: 'VALVE_RECOVERY_FAIL_CLOSED',
    description: 'Always plays it safe: if in doubt, shuts it off.',
    points: [
      'If the valve\'s position can\'t be confirmed, it drives fully closed and holds '
      + 'there — no attempt to guess where it should go.',
      'This happens even if the valve was last known open, or was deliberately opened by someone.',
      'The safest choice for a shutoff valve when uncertainty is the main risk, at the cost of '
      + 'possibly closing a valve someone left open on purpose.',
    ],
  },
  2: {
    label: 'Restore last confirmed (default)',
    name: 'VALVE_RECOVERY_RESTORE_LAST',
    description: 'Default policy for Large valve. Attempts to put the valve back exactly where it was last confirmed.',
    points: [
      'A quick check (a couple of seconds) first sees if the valve is already where it was last confirmed.',
      'A valve that looks cleanly moved to the other end is accepted as the new position rather than reversed.',
      'Otherwise, a valve stuck somewhere in between is driven back to its last confirmed '
      + 'position — this can take up to 30–40 seconds.',
      'Balances restoring what should be there against respecting a deliberate change, at the '
      + 'cost of undoing a genuine manual move left mid-travel.',
    ],
  },
  3: {
    label: 'Small valve seek',
    name: 'VALVE_RECOVERY_SMALL_SEEK',
    description:
      'Default policy for Small valve. A gentler, trial-and-error approach for smaller valves that can only confirm their '
      + 'position while actively being driven.',
    points: [
      'Briefly drives toward the last known position first, and checks if that confirms it.',
      'If not, tries driving the other way for a bit longer, and checks again.',
      'This repeats a few times, trying slightly longer each round.',
      'If nothing confirms, it falls back to driving fully closed as a safety net.',
      'If even that fails, the valve is left as-is and flagged unknown, rather than run the motor indefinitely.',
    ],
  },
}

export const VALVE_MANUFACTURERS: ValveManufacturer[] = ['KLD', 'AVA']

export const VALVE_SIZES: ValveSize[] = [
  'DN20',
  'DN25',
  'DN32',
  'DN40',
  'DN50',
  'DN65',
  'DN80',
]

// Valve response models opt into camelCase while the surrounding device fields remain
// snake_case.
export interface ValveSettingsAPI {
  valveId: ValveKey
  name?: string | null
  manufacturer?: ValveManufacturer | null
  size?: ValveSize | null
}

export type ValveSettingsPatch = Pick<
  ValveSettingsAPI,
  'name' | 'manufacturer' | 'size'
>

export type ValveSettingsField = keyof ValveSettingsPatch

export interface DevicePowerAPI {
  HUB_BAT: number
  HUB_STA: string
  CSQ: number
  ext_battery: number
}

export interface DeviceSWAPI {
  HUB_SW: string
  HUB_HW: string
  SW_868: string
  HW_868: string
}

export interface DeviceNetworkAPI {
  IMSI: string
  IMEI: string
  ICCID: string
  network_name?: string
  fplmn_list?: string
}

export interface DeviceStatusAPI {
  device_id?: string
  ping?: string
  // Replaces the flat valveState map (V1–V5 software state only), which is still served
  // but no longer read. Always populated — backfilled on boot where the device has not
  // reported true extended state.
  valveStateExtended: ValveStateExtendedSlots
  // Optional and nullable, matching `DeviceStatusPublic` in the API schema, whose only
  // required members are `valveState`, `valveStateExtended` and `valveRecovery`. Declaring
  // these three as present was the client asserting a guarantee the API never made, and it
  // hid the unguarded reads in mapDevice from tsc — a `null` here threw inside the query
  // function, which fails the whole `/devices` request rather than one device, and does not
  // self-heal under `staleTime: Infinity`.
  power?: DevicePowerAPI | null
  device_sw?: DeviceSWAPI | null
  network_info?: DeviceNetworkAPI | null
  bin_file_name?: string
  bin_file_path?: string
  on_batt_date?: string
  last_ping?: string
  mnc?: string
  mcc?: string
  valveRecovery?: number | null
}

export interface DeviceSettingsAPI {
  id: number
  // camelCase although every other key here is snake_case — it is what the API returns.
  deviceState: DeviceState
  data_acq_mode: number
  server_pulse_control: boolean
  pulses_per_liter: number
  firmware_version_desired?: string
  timezone: string
  install_date: string
  usage_start_date: string
  monitoring_start_date: string
  device_id_activation_date: string
  currency: string
  cost: number
  report_30_day: boolean
  esg_report: boolean
  sector_type: string
  master_device_id_ref?: number
  occupants: number | null
  hot: boolean
  device_name: string
  device_name_short?: string
  upload_freq_mins: number
  location?: string
  uom: string
  conversion_factor: number
  monitoring_enabled: boolean
  aas_enabled: boolean
  co2_rate: number
  call_date_30_day: string
  sustainability_report: boolean
  set_up_payment: boolean
  renewal_overdue_status: boolean
  client_meter_id?: string
  usage_logging_enabled: boolean
  vacant: boolean
  no_flow_threshold_hours: number
  missed_checkin_threshold: number
  has_valve_schedule: boolean
}

// Fields the API models on one shared base for every device type — provisioned
// (`DevicePublic`), test (`TestDevicePublic`) and staged (`StagedDevicePublic`). Anything
// added to the base server-side lands on all three at once, so it belongs here rather than
// repeated per interface.
export interface DeviceBaseAPI {
  device_id: string
  state: string
  model: string
  vendor: string
  // Required and non-null on provisioned devices only; `DeviceAPI` narrows it.
  vendor_id?: string | null
  valve_operation: boolean
  rtf_operation: boolean
  hlf_operation: boolean
  reset_operation: boolean
  fw_operation: boolean
  status_operation: boolean
  azure_deleted?: boolean | null
  azure_deleted_date?: string | null
}

// Test and staged devices carry the raw `DeviceStatusInDB` shape, not the camelCased
// `DeviceStatusPublic` that `/devices` returns. Same data, different key casing, so the two
// cannot share a type.
export type ValveStateExtendedSlotsInDB = {
  [Key in ValveKey as `${Lowercase<Key>}_extended`]: ValveStateExtendedAPI | null
}

export const valveExtendedKeyInDB = (key: ValveKey) =>
  `${key.toLowerCase()}_extended` as keyof ValveStateExtendedSlotsInDB

export interface DeviceStatusInDBAPI {
  device_id: string
  connection_state?: string | null
  last_ping?: string | null
  valve_state?: { [Key in ValveKey]?: ValveState | null } | null
  valve_state_extended?: ValveStateExtendedSlotsInDB | null
  power?: DevicePowerAPI | null
  device_sw?: DeviceSWAPI | null
  network_info?: DeviceNetworkAPI | null
  bin_file_name?: string | null
  bin_file_path?: string | null
  on_batt_date?: string | null
  mnc?: number | null
  mcc?: number | null
  valve_recovery?: number | null
}

// `TestDevicePublic` and `StagedDevicePublic` are separate schemas with identical shapes:
// the shared base plus firmware and raw status. Declared as siblings, not one aliasing the
// other, matching the API — so a staged payload is never typed as a test one. Both dropped
// `device_settings`, so neither carries a device name; the device ID is the only label.
export interface TestDeviceAPI extends DeviceBaseAPI {
  firmware_version?: string | null
  device_status?: DeviceStatusInDBAPI | null
}

export interface StagedDeviceAPI extends DeviceBaseAPI {
  firmware_version?: string | null
  device_status?: DeviceStatusInDBAPI | null
}

export interface DeviceAPI extends DeviceBaseAPI {
  id: number
  dl_id: number
  created_at: string
  updated_at?: string | null
  vendor_id: string
  d_uuid: string
  device_settings: DeviceSettingsAPI
  // Optional and nullable, matching `DevicePublic` — `device_status` is not in its required
  // set. The client used to declare it present, which hid the unguarded reads in mapDevice
  // from tsc.
  device_status?: DeviceStatusAPI | null
  customer_location_id?: number | null
  account_id?: number | null
  child_device_ids?: number[] | null
  comments: boolean | null
  stripe_subscription_id?: string | null
  subscription_status?: string | null
}

export interface conversionFactors {
  Liters: number
  'Gallons(US)': number
  'Cubic Feet': number
  'Cubic Meters': number
  'Gallons(IMP)': number
}
